/**
 * useAuditStore — Zustand store replacing auditDataService singleton + eventService broadcasts
 *
 * In AngularJS, audit state is managed via:
 * 1. auditDataService singleton properties (results, taskItems, orderNumber, coreScript)
 * 2. eventService.eventBroadcast() for cross-component communication
 * 3. $rootScope properties (G_WORKORDER, G_PERMISSIONS, etc.)
 *
 * This Zustand store replaces patterns 1 and 2.
 * Pattern 3 continues to use getRootScopeProperty() bridge.
 *
 * Event → Store Action mapping:
 * - AUDIT_RESULTS_DATA → setResults(data)
 * - AUDIT_TASK_DATA → setTaskItems(data) (triggered after ListTasksByWorkflow)
 * - AUDIT_BILLING_TOTALS → setBillingTotals(client, vendor)
 * - AUDIT_TASKITEMS_UPDATE → triggerTaskItemsUpdate (subscribers re-read)
 * - AUDIT_TASKITEM_CATEGORYNAME → setCurrentCategoryName(name)
 * - AUDIT_NOCHARGEPAYTASK_MODAL → setNoChargePayModalVisible(visible)
 * - AUDIT_MODIFYTASK_MODAL → setModifyTaskModalVisible(visible)
 * - AUDIT_TASK_MODAL → setTaskWorkFlowModalVisible(visible)
 */

import { create } from 'zustand';
import type { AuditResult } from './auditApi';

// ============ TYPES ============

export interface TaskItemCategory {
  externalName?: string;
  tasks: TaskItem[];
}

export interface TaskItem {
  id: number;
  category?: string;
  type?: string;
  requiresAudit?: boolean;
  externalAutomationData?: string;
  sourceVendorTotal?: number;
  workflowStateTransitionId?: number;
  allowedTransitions?: {
    toStateId: number;
    name: string;
    transPermission?: string;
  }[];
  billingLineItems: {
    clientTotal: number;
    vendorTotal: number;
    [key: string]: unknown;
  }[];
  [key: string]: unknown;
}

export interface CoreScriptState {
  Complete: boolean;
  CompleteContext: number;
  ReadOnlyMode: boolean;
  Followup: boolean;
}

export interface AuditStoreState {
  // Data — replaces auditDataService singleton properties
  results: AuditResult[];
  taskItems: TaskItemCategory[];
  orderNumber: number | null;
  coreScript: CoreScriptState;
  scriptDictionary: Record<string, unknown> | null;

  // Billing totals — replaces AUDIT_BILLING_TOTALS event
  billingTotals: {
    clientTotal: number;
    vendorTotal: number;
  };

  // Modal visibility — replaces event-based modal orchestration
  modifyTaskModalVisible: boolean;
  taskWorkFlowModalVisible: boolean;
  noChargePayModalVisible: boolean;
  currentCategoryName: string;

  // Selected task item (for modal context)
  selectedTaskItem: TaskItem | null;
  selectedAction: string;

  // Update counter — triggers re-reads (replaces AUDIT_TASKITEMS_UPDATE)
  taskItemsUpdateCounter: number;

  // Actions
  setResults: (results: AuditResult[]) => void;
  setTaskItems: (taskItems: TaskItemCategory[]) => void;
  setOrderNumber: (orderNumber: number | null) => void;
  setBillingTotals: (clientTotal: number, vendorTotal: number) => void;
  setModifyTaskModalVisible: (visible: boolean) => void;
  setTaskWorkFlowModalVisible: (visible: boolean) => void;
  setNoChargePayModalVisible: (visible: boolean) => void;
  setCurrentCategoryName: (name: string) => void;
  setSelectedTaskItem: (item: TaskItem | null, action?: string) => void;
  triggerTaskItemsUpdate: () => void;
  coreScriptReset: () => void;
  calculateBillingTotals: () => void;
}

// ============ STORE ============

export const useAuditStore = create<AuditStoreState>((set, get) => ({
  // Initial state — matches auditDataService defaults
  results: [],
  taskItems: [],
  orderNumber: null,
  coreScript: {
    Complete: false,
    CompleteContext: 0,
    ReadOnlyMode: false,
    Followup: false,
  },
  scriptDictionary: null,

  billingTotals: {
    clientTotal: 0,
    vendorTotal: 0,
  },

  modifyTaskModalVisible: false,
  taskWorkFlowModalVisible: false,
  noChargePayModalVisible: false,
  currentCategoryName: '',

  selectedTaskItem: null,
  selectedAction: '',

  taskItemsUpdateCounter: 0,

  // Actions

  setResults: (results) => set({ results }),

  setTaskItems: (taskItems) => set({ taskItems }),

  setOrderNumber: (orderNumber) => set({ orderNumber }),

  setBillingTotals: (clientTotal, vendorTotal) =>
    set({ billingTotals: { clientTotal, vendorTotal } }),

  setModifyTaskModalVisible: (visible) => set({ modifyTaskModalVisible: visible }),

  setTaskWorkFlowModalVisible: (visible) => set({ taskWorkFlowModalVisible: visible }),

  setNoChargePayModalVisible: (visible) => set({ noChargePayModalVisible: visible }),

  setCurrentCategoryName: (name) => set({ currentCategoryName: name }),

  setSelectedTaskItem: (item, action = '') =>
    set({ selectedTaskItem: item, selectedAction: action }),

  triggerTaskItemsUpdate: () =>
    set((state) => ({ taskItemsUpdateCounter: state.taskItemsUpdateCounter + 1 })),

  // coreScriptReset — matches auditDataService.coreScriptReset()
  coreScriptReset: () =>
    set({
      results: [],
      coreScript: {
        Complete: false,
        CompleteContext: 0,
        ReadOnlyMode: false,
        Followup: false,
      },
    }),

  // calculateBillingTotals — matches getClientAndVendorTotals() used across controllers
  calculateBillingTotals: () => {
    const { taskItems } = get();
    let clientTotal = 0;
    let vendorTotal = 0;

    taskItems.forEach((category) => {
      category.tasks.forEach((task) => {
        task.billingLineItems.forEach((billingLineItem) => {
          clientTotal += billingLineItem.clientTotal;
          vendorTotal += billingLineItem.vendorTotal;
        });
      });
    });

    set({ billingTotals: { clientTotal, vendorTotal } });
  },
}));
